import { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getCollection } from 'store/reducers/content';
import { setContent } from 'actions/content-actions';
import graphql from 'api/graphql';
import logger from 'util/logger';
import { Config } from 'api/interfaces';

interface Data {
  contentTypeId: string;
  slug?: string;
  subSlug?: string;
  query?: unknown;
  onError?: (error: unknown) => void;
  options?: string[];
  config?: Config;
  store?: boolean;
  refetchDeps?: Array<string | number>;
}

interface UseFetchData {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  isLoading: boolean;
  error?: boolean;
  loadMore: () => void;
}

export default function useFetchData({
  contentTypeId,
  slug,
  subSlug,
  query,
  onError,
  options,
  config,

  store,
  refetchDeps = [],
}: Data): UseFetchData {
  const [state, setState] = useState({ isLoading: !!slug, data: null, error: false });
  const dispatch = useDispatch();

  const dataRef = useRef(null);

  const dataFromStore = useSelector(getCollection(slug));

  useEffect(() => {
    let unmounted: boolean;

    async function fetchData() {
      try {
        const fetchFromGraphql = graphql.getFetchFunction(contentTypeId);

        const data = await fetchFromGraphql({
          slug,
          subSlug,
          query,
          // Remove contentTypeId after overview query cleanup
          contentTypeId,
          options,
          config,
        });
        if (!unmounted) {
          if (store) {
            const collection = {};
            collection[slug] = data;
            dispatch(setContent(collection, contentTypeId));
          }
          dataRef.current = data;
          setState({ data, error: false, isLoading: false });
        }
      } catch (graphqlError) {
        if (!unmounted) {
          dataRef.current = null;
          setState({ data: null, error: true, isLoading: false });
        }
        if (onError) {
          onError(slug);
        }
        logger.error(`Error ${graphqlError} while fetchData with graphQL for ${slug} | ${contentTypeId}`);
      }
    }

    if (slug && contentTypeId) {
      if (dataFromStore) {
        setState({ data: dataFromStore, error: false, isLoading: false });
      } else {
        fetchData();
      }
    }

    return () => {
      unmounted = true;
    };
  }, [slug, subSlug, query, ...refetchDeps]);

  const loadMore = useCallback(async () => {
    try {
      setState({ ...state, data: dataRef.current, isLoading: true });
      const fetchFromGraphql = graphql.getFetchFunction(contentTypeId);
      const data = await fetchFromGraphql({
        slug,
        subSlug,
        query,
        // Remove contentTypeId after overview query cleanup
        contentTypeId,
        options,
        config,
        data: dataRef.current,
      });

      if (store) {
        const collection = {};
        collection[slug] = data;
        dispatch(setContent(collection, contentTypeId));
      }
      dataRef.current = data;
      setState({ data, error: false, isLoading: false });
    } catch (graphqlError) {
      setState({ data: null, error: true, isLoading: false });
      if (onError) {
        onError(slug);
      }
      logger.error(`Error ${graphqlError} while fetchData with graphQL for ${slug} | ${contentTypeId}`);
    }
  }, [state]);

  return {
    ...state,
    loadMore,
  };
}
